/**
 * The A/B transport of the voice comparison on two real audio elements: one `useAudioPlayback`
 * per side, driven by the pure transport (`voice-ab.ts`). A play starts inside the tap that asks
 * for it — a browser plays only from a tap — and a side that has to fall silent is paused once
 * its element shows that it sounds (`abOrder`). A side whose clip is not here yet is asked for
 * on its first play (every fetch of a clip is recorded, so nothing is fetched on opening) and
 * plays once the clip arrives, if the browser still lets it.
 */
import { useEffect, useRef, useState } from 'react'
import type { RecordingAudioState } from '@/components/humint/questioning/recording-types'
import { useStableCallback } from '@/components/humint/questioning/use-stable-callback'
import {
  VOICE_PAIR_SIDES,
  type VoicePairSideState,
  type VoicePairTransport,
} from '@/components/humint/voice/match/voice-pair-model'
import type { VoicePairSideId } from '@/components/humint/voice/match/voice-pair-types'
import { type AudioPlayback, useAudioPlayback } from '../recording/useAudioPlayback'
import {
  type AbEvent,
  type AbFlight,
  type AbState,
  abHeard,
  abInitial,
  abOrder,
  abPlayback,
  abReduce,
  abSounding,
} from './voice-ab'

/** One side's clip as the page holds it. */
export interface VoiceAbSource {
  /** The object URL of the fetched clip; null until it is here. */
  src: string | null
  /** How the fetch stands; `unavailable` also for a side with no clip at all. */
  audio: RecordingAudioState
  /** The clip's length as the read gave it, until the element knows its own. */
  lengthMs: number | null
}

export type VoiceAbSources = Record<VoicePairSideId, VoiceAbSource>

/** A play that has not started sounding by then is taken as refused by the browser. */
const PLAY_START_TIMEOUT_MS = 2_000

type Sides<T> = Record<VoicePairSideId, T>

/** The two elements and how each side's clip stands. */
interface AbElements {
  players: Sides<AudioPlayback>
  audio: Sides<RecordingAudioState>
}

type Apply = (event: AbEvent) => { before: AbState; after: AbState }

const audioOf = (source: VoiceAbSource, player: AudioPlayback): RecordingAudioState =>
  player.failed ? 'unavailable' : source.audio

/** The first side that can be played: the transport opens on it. */
const firstPlayable = (sources: VoiceAbSources): VoicePairSideId =>
  sources.a.audio === 'unavailable' && sources.b.audio !== 'unavailable' ? 'b' : 'a'

/**
 * The transport's state with a way to change it that answers at once: an event taken inside a
 * tap is reduced before the tap ends, so the element can be started in it.
 */
function useAbState(sources: VoiceAbSources): { state: AbState; apply: Apply } {
  const [state, setState] = useState<AbState>(() =>
    abInitial({ a: sources.a.lengthMs, b: sources.b.lengthMs }, firstPlayable(sources))
  )
  const latest = useRef(state)
  const apply = useStableCallback((event: AbEvent) => {
    const before = latest.current
    const after = abReduce(before, event)
    latest.current = after
    setState(after)
    return { before, after }
  })
  return { state, apply }
}

function useAbElements(sources: VoiceAbSources): AbElements {
  const a = useAudioPlayback(sources.a.src, sources.a.lengthMs ?? 0)
  const b = useAudioPlayback(sources.b.src, sources.b.lengthMs ?? 0)
  return { players: { a, b }, audio: { a: audioOf(sources.a, a), b: audioOf(sources.b, b) } }
}

/** Commands in flight per side, and the wait for a play that may never start. */
function useFlights(onNeverStarted: (side: VoicePairSideId) => void) {
  const flights = useRef<Sides<AbFlight>>({ a: null, b: null })
  const timers = useRef<Sides<ReturnType<typeof setTimeout> | null>>({ a: null, b: null })
  const giveUp = useStableCallback((side: VoicePairSideId) => {
    timers.current[side] = null
    if (flights.current[side] !== 'play') return
    flights.current[side] = null
    onNeverStarted(side)
  })
  const set = useStableCallback((side: VoicePairSideId, flight: AbFlight) => {
    const timer = timers.current[side]
    if (timer && flight !== 'play') {
      clearTimeout(timer)
      timers.current[side] = null
    }
    if (flight === 'play' && !timers.current[side]) {
      timers.current[side] = setTimeout(giveUp, PLAY_START_TIMEOUT_MS, side)
    }
    flights.current[side] = flight
  })
  useEffect(
    () => () => {
      for (const side of VOICE_PAIR_SIDES) clearTimeout(timers.current[side] ?? undefined)
    },
    []
  )
  return { flights, set }
}

/** What each element reports of itself goes into the transport. */
function useReadouts({ players }: AbElements, apply: Apply) {
  const a = players.a.playback
  const b = players.b.playback
  useEffect(() => {
    apply({ type: 'progress', side: 'a', positionMs: a.positionMs, durationMs: a.durationMs })
  }, [apply, a.positionMs, a.durationMs])
  useEffect(() => {
    apply({ type: 'progress', side: 'b', positionMs: b.positionMs, durationMs: b.durationMs })
  }, [apply, b.positionMs, b.durationMs])
}

/** After every render: hold each element to what the transport wants of it. */
function useHeldElements(
  { state, apply }: { state: AbState; apply: Apply },
  { players, audio }: AbElements,
  flight: ReturnType<typeof useFlights>
) {
  useEffect(() => {
    for (const side of VOICE_PAIR_SIDES) {
      const next = abOrder({
        wanted: abSounding(state, side),
        audio: audio[side],
        playing: players[side].playback.playing,
        flight: flight.flights.current[side],
      })
      flight.set(side, next.flight)
      if (next.order === 'pause') players[side].toggle()
      if (next.order === 'play') players[side].playFrom(state.sides[side].positionMs)
      if (next.order === 'stopped') apply({ type: 'stopped', side })
    }
  })
}

/**
 * `sources`: the two clips as far as they are fetched. `onRequest`: ask for a side's clip, called
 * when a side that has none yet is to play. Gives what `VoicePairView` takes as its transport.
 */
export function useVoiceAb(
  sources: VoiceAbSources,
  onRequest?: (side: VoicePairSideId) => void
): Pick<VoicePairTransport, 'active' | 'sides' | 'actions'> {
  const elements = useAbElements(sources)
  const { players, audio } = elements
  const { state, apply } = useAbState(sources)
  const flight = useFlights(side => apply({ type: 'stopped', side }))
  useReadouts(elements, apply)

  /** Inside the tap: start the side that is to sound now, or ask for its clip. */
  const act = useStableCallback((event: AbEvent) => {
    const { before, after } = apply(event)
    if (event.type === 'seek') players[event.side].seek(after.sides[event.side].positionMs)
    const side = after.active
    if (!abSounding(after, side) || abSounding(before, side)) return
    if (audio[side] === 'ready') {
      players[side].playFrom(after.sides[side].positionMs)
      flight.set(side, 'play')
    } else if (audio[side] !== 'unavailable') {
      flight.set(side, 'owed')
      if (audio[side] === 'none') onRequest?.(side)
    }
  })

  useHeldElements({ state, apply }, elements, flight)

  const sideState = (side: VoicePairSideId): VoicePairSideState => ({
    audio: audio[side],
    playback: abPlayback(state, side),
    heard: abHeard(state, side),
  })
  return {
    active: state.active,
    sides: [sideState('a'), sideState('b')],
    actions: {
      onSelect: side => act({ type: 'select', side }),
      onTogglePlay: () => act({ type: 'toggle' }),
      onSeek: (side, ms) => act({ type: 'seek', side, ms }),
    },
  }
}
